import React, { useState, useEffect } from "react";
import {
  Modal, Box, Typography, TextField, Button, Backdrop, CircularProgress,
  Chip, Stack
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import { addSubscription } from "../../../Reducer/Subscription/subscriptionPlanSlice";

const modalStyle = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400, bgcolor: 'background.paper',
  borderRadius: 2, boxShadow: 24, p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
};

const AddNewPlans = ({ open = false, onClose = () => {}, onAdd = () => {} }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    title: '',
    duration_months: '',
    price_usd: '',
    product_limit_per_month: 1,
    description: '',
    highlight_tag: ''
  });

  const [features, setFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState("");

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [apiError, setApiError] = useState("");

  const resetForm = () => {
    setLoading(false);
    setErrors({});
    setSuccessMsg("");
    setApiError("");
    setFormData({
      title: '',
      duration_months: '',
      price_usd: '',
      product_limit_per_month: 1,
      description: '',
      highlight_tag: ''
    });
    setFeatures([]);
    setNewFeature("");
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddFeature = () => {
    const trimmed = newFeature.trim();
    if (trimmed && !features.includes(trimmed)) {
      setFeatures([...features, trimmed]);
      setNewFeature("");
    }
  };

  const handleRemoveFeature = (featureToRemove) => {
    setFeatures(features.filter(feature => feature !== featureToRemove));
  };

  const handleFeatureKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFeature();
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.duration_months || isNaN(Number(formData.duration_months))) newErrors.duration_months = 'Duration is required';
    if (!formData.price_usd || isNaN(Number(formData.price_usd))) newErrors.price_usd = 'Price is required';
    if (features.length === 0) newErrors.features = 'At least one feature is required';
    if (!formData.product_limit_per_month || isNaN(Number(formData.product_limit_per_month))) newErrors.product_limit_per_month = 'Product limit is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setApiError("");
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        duration_months: Number(formData.duration_months),
        price_usd: Number(formData.price_usd),
        features,
        product_limit_per_month: Number(formData.product_limit_per_month),
        description: formData.description,
        highlight_tag: formData.highlight_tag || null
      };

      const BASE_URL = process.env.REACT_APP_API_URL || "";
      const response = await fetch(`${BASE_URL}/api/v1/subscription-plan/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (response.ok) {
        setSuccessMsg("Plan added successfully!");
        dispatch(addSubscription(result.plan || result));
        onAdd(result.plan || result);
        setTimeout(() => onClose(), 1000);
      } else {
        setApiError(result.error || "Failed to create plan");
      }
    } catch (err) {
      console.error("Error creating plan:", err);
      setApiError("Network or server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} aria-labelledby="add-plan-modal">
        <Box sx={modalStyle} component="form" onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>Add New Plan</Typography>

          {successMsg && <Typography color="success.main" sx={{ mb: 1 }}>{successMsg}</Typography>}
          {apiError && <Typography color="error.main" sx={{ mb: 1 }}>{apiError}</Typography>}

          <TextField
            label="Plan Title" name="title" fullWidth margin="normal"
            value={formData.title} onChange={handleChange}
            error={!!errors.title} helperText={errors.title}
            autoFocus autoComplete="off"
          />
          <TextField
            label="Duration (months)" name="duration_months" fullWidth margin="normal"
            value={formData.duration_months} onChange={handleChange}
            type="number" autoComplete="off"
            error={!!errors.duration_months} helperText={errors.duration_months}
          />
          <TextField
            label="Price (USD)" name="price_usd" fullWidth margin="normal"
            value={formData.price_usd} onChange={handleChange}
            type="number" autoComplete="off"
            error={!!errors.price_usd} helperText={errors.price_usd}
          />

          {/* Features Section */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Features</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth size="small" placeholder="Enter a new feature"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={handleFeatureKeyPress}
                error={!!errors.features}
                helperText={errors.features}
              />
              <Button
                variant="outlined" size="small"
                onClick={handleAddFeature}
                disabled={!newFeature.trim() || features.includes(newFeature.trim())}
                startIcon={<AddIcon />}
              >
                ADD
              </Button>
            </Box>
            {features.includes(newFeature.trim()) && (
              <Typography variant="caption" color="warning.main">
                Feature already added.
              </Typography>
            )}
            {features.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {features.map((feature, index) => (
                  <Chip
                    key={index} label={feature}
                    onDelete={() => handleRemoveFeature(feature)}
                    deleteIcon={<DeleteIcon />} variant="outlined" size="small" sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No features added yet
              </Typography>
            )}
          </Box>

          <TextField
            label="Product Limit Per Month" name="product_limit_per_month"
            fullWidth margin="normal" type="number" autoComplete="off"
            value={formData.product_limit_per_month} onChange={handleChange}
            error={!!errors.product_limit_per_month} helperText={errors.product_limit_per_month}
          />
          <TextField
            label="Description" name="description"
            fullWidth margin="normal"
            value={formData.description} onChange={handleChange}
            error={!!errors.description} helperText={errors.description}
          />
          <TextField
            label="Highlight Tag (optional)" name="highlight_tag"
            fullWidth margin="normal"
            value={formData.highlight_tag} onChange={handleChange}
          />

          <Button variant="contained" fullWidth type="submit" sx={{ mt: 2 }} disabled={loading}>
            {loading ? 'Adding...' : 'Add Plan'}
          </Button>
        </Box>
      </Modal>

      <Backdrop open={loading} sx={{ zIndex: 1300, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default AddNewPlans;
